import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { LogIn, LogOut, RefreshCw, Search } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { useVisitorScope } from "../../components/visitors/useVisitorScope";
import { NoFacilitiesNotice, PageHeader, ScopeFilters } from "../../components/visitors/VisitorShared";
import { CheckInDialog } from "../../components/visitors/VisitorForms";
import { VisitDetailDialog } from "../../components/visitors/VisitDetailDialog";
import { fmtDuration, fmtTime, visitorApi } from "../../components/visitors/visitorUtils";

export default function AccessControl() {
  const { t } = useTranslation();
  const scope = useVisitorScope();
  const [inside, setInside] = useState([]);
  const [search, setSearch] = useState("");
  const [checkInOpen, setCheckInOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setInside(await visitorApi.get("/visits/inside", scope.params));
    } catch (e) {
      console.error("Error cargando visitantes dentro:", e);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope.companyId, scope.facilityId]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return inside;
    return inside.filter((v) =>
      [v.visitor_name, v.visitor_id_number, v.visitor_company, v.host_name, v.vehicle?.plate]
        .filter(Boolean)
        .some((s) => s.toLowerCase().includes(q))
    );
  }, [inside, search]);

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("visitors.access.title")}
        subtitle={t("visitors.access.subtitle")}
        actions={
          <>
            <Button variant="outline" size="icon" onClick={load} aria-label={t("visitors.common.refresh")}>
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
            <Button size="lg" onClick={() => setCheckInOpen(true)} disabled={scope.facilities.length === 0}>
              <LogIn className="h-5 w-5 mr-2" />
              {t("visitors.access.newEntry")}
            </Button>
          </>
        }
      />

      <ScopeFilters scope={scope}>
        <div className="lg:col-span-2">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              className="pl-9"
              placeholder={t("visitors.common.searchPlaceholder")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
      </ScopeFilters>
      {scope.loaded && scope.facilities.length === 0 && <NoFacilitiesNotice isManager={scope.isManager} />}

      <Card>
        <CardHeader>
          <CardTitle>
            {t("visitors.dashboard.peopleInside")} ({inside.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filtered.length === 0 ? (
            <p className="text-center py-8 text-slate-500">{t("visitors.dashboard.nobodyInside")}</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("visitors.common.visitor")}</TableHead>
                  <TableHead className="hidden md:table-cell">{t("visitors.common.facility")}</TableHead>
                  <TableHead className="hidden md:table-cell">{t("visitors.common.host")}</TableHead>
                  <TableHead className="hidden lg:table-cell">{t("visitors.common.vehicle")}</TableHead>
                  <TableHead>{t("visitors.common.checkIn")}</TableHead>
                  <TableHead className="text-right">{t("visitors.common.actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((v) => (
                  <TableRow key={v.id} className="cursor-pointer" onClick={() => setDetailId(v.id)}>
                    <TableCell>
                      <p className="font-medium">{v.visitor_name}</p>
                      <p className="text-xs text-slate-500">{[v.visitor_id_number, v.visitor_company].filter(Boolean).join(" · ")}</p>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      {v.facility_name}
                      {v.access_point && <span className="text-xs text-slate-500"> · {v.access_point}</span>}
                    </TableCell>
                    <TableCell className="hidden md:table-cell">{v.host_name || "—"}</TableCell>
                    <TableCell className="hidden lg:table-cell">{v.vehicle?.plate || "—"}</TableCell>
                    <TableCell>
                      <p>{fmtTime(v.check_in_at)}</p>
                      <p className="text-xs text-slate-500">{fmtDuration(v.minutes_inside)}</p>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDetailId(v.id);
                        }}
                      >
                        <LogOut className="h-4 w-4 sm:mr-2" />
                        <span className="hidden sm:inline">{t("visitors.common.checkOut")}</span>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <CheckInDialog open={checkInOpen} onOpenChange={setCheckInOpen} scope={scope} onDone={load} />
      <VisitDetailDialog
        visitId={detailId}
        open={!!detailId}
        onOpenChange={(o) => !o && setDetailId(null)}
        scope={scope}
        onChanged={load}
      />
    </div>
  );
}
